import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import { maskCEP, unmaskCEP } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { Endereco } from "../interface/Paciente.interface";
import { CriarPacienteFormData } from "../schemas/paciente.schema";

const UFS_BRASIL = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

interface AbaEnderecosProps {
  form: UseFormReturn<CriarPacienteFormData>;
  formData: CriarPacienteFormData;
}

export const AbaEnderecos = ({ 
  form,
  formData
}: AbaEnderecosProps) => {
  const { toast } = useToast();
  // Refs para armazenar os timeouts de debounce por índice de endereço
  const debounceTimeouts = useRef<Record<number, NodeJS.Timeout>>({});

  // Métodos para gerenciar endereços
  const adicionarEndereco = () => {
    const enderecosAtuais = form.getValues("enderecos") || [];
    form.setValue("enderecos", [
      ...enderecosAtuais,
      {
        cep: "",
        logradouro: "",
        numero: "",
        bairro: "",
        cidade: "",
        uf: "",
        complemento: "",
        principal: enderecosAtuais.length === 0,
      },
    ]);
  };

  const removerEndereco = (index: number) => {
    const enderecosAtuais = form.getValues("enderecos") || [];
    const novosEnderecos = enderecosAtuais.filter((_, i) => i !== index);
    form.setValue("enderecos", novosEnderecos);
  };

  const atualizarEndereco = (index: number, campo: keyof Endereco, valor: string | boolean) => {
    const enderecosAtuais = form.getValues("enderecos") || [];
    const novosEnderecos = [...enderecosAtuais];
    novosEnderecos[index] = { ...novosEnderecos[index], [campo]: valor } as Endereco;
    form.setValue("enderecos", novosEnderecos);
  };

  const buscarCep = async (cep: string, index: number) => {
    try {
      // Remove formatação do CEP
      const cepLimpo = cep.replace(/\D/g, '');
      
      // Verifica se o CEP tem 8 dígitos
      if (cepLimpo.length !== 8) {
        return;
      }

      const response: { result: any } = await api.get(`/api/cep/${cepLimpo}`);
      
      if (response.result && !response.result.erro) {
        // Preenche os campos do endereço com os dados retornados
        const dadosCep = response.result;
        
        // Atualiza todos os campos de uma vez
        const enderecosAtuais = form.getValues("enderecos") || [];
        const novosEnderecos = [...enderecosAtuais];
        novosEnderecos[index] = {
          ...novosEnderecos[index],
          logradouro: dadosCep.logradouro || '',
          bairro: dadosCep.bairro || '',
          cidade: dadosCep.localidade || '',
          uf: dadosCep.uf || '',
          complemento: dadosCep.complemento || '',
        };
        form.setValue("enderecos", novosEnderecos);
        
        toast({
          title: "CEP encontrado",
          description: "Endereço preenchido automaticamente",
        });
      } else {
        toast({
          title: "CEP não encontrado",
          description: "Verifique se o CEP está correto",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Erro ao buscar CEP:", error);
      toast({
        title: "Erro",
        description: error?.message || "Erro ao buscar o CEP",
        variant: "destructive",
      });
    }
  };

  const handleCepChange = (index: number, value: string) => {
    // Atualiza o valor do CEP
    atualizarEndereco(index, "cep", unmaskCEP(value));
    
    // Remove o timeout anterior se existir
    if (debounceTimeouts.current[index]) {
      clearTimeout(debounceTimeouts.current[index]);
    }
    
    // Remove formatação para verificar se tem 8 dígitos
    const cepLimpo = unmaskCEP(value);
    
    // Se tiver 8 dígitos, aguarda 500ms sem digitar para fazer a busca
    if (cepLimpo.length === 8) {
      debounceTimeouts.current[index] = setTimeout(() => {
        buscarCep(cepLimpo, index);
      }, 500);
    }
  };

  // Limpa os timeouts ao desmontar o componente
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Endereços</CardTitle>
          <Button type="button" onClick={adicionarEndereco} size="sm">
            <Plus className="h-4 w-4 mr-2" /> Adicionar Endereço
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {(formData.enderecos || []).map((endereco, index) => (
          <Card key={index} className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Endereço {index + 1}</h4>
              <Button type="button" variant="ghost" size="sm" onClick={() => removerEndereco(index)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name={`enderecos.${index}.cep`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CEP</FormLabel>
                    <FormControl>
                      <Input 
                        value={maskCEP(field.value || '')} 
                        onChange={(e) => {
                          const valor = unmaskCEP(e.target.value);
                          field.onChange(valor);
                          handleCepChange(index, e.target.value);
                        }} 
                        placeholder="00000-000" 
                        maxLength={9}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`enderecos.${index}.numero`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número</FormLabel>
                    <FormControl>
                      <Input 
                        value={field.value || ""} 
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          atualizarEndereco(index, "numero", e.target.value);
                        }} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`enderecos.${index}.logradouro`}
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Logradouro</FormLabel>
                    <FormControl>
                      <Input 
                        value={field.value || ""} 
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          atualizarEndereco(index, "logradouro", e.target.value);
                        }} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`enderecos.${index}.bairro`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bairro</FormLabel>
                    <FormControl>
                      <Input 
                        value={field.value || ""} 
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          atualizarEndereco(index, "bairro", e.target.value);
                        }} 
                        placeholder="Nome do bairro" 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`enderecos.${index}.cidade`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cidade</FormLabel>
                    <FormControl>
                      <Input 
                        value={field.value || ""} 
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          atualizarEndereco(index, "cidade", e.target.value);
                        }} 
                        placeholder="Nome da cidade" 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`enderecos.${index}.uf`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UF</FormLabel>
                    <Select
                      value={field.value || ""}
                      onValueChange={(value) => {
                        field.onChange(value);
                        atualizarEndereco(index, "uf", value);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a UF" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {UFS_BRASIL.map((uf) => (
                          <SelectItem key={uf} value={uf}>
                            {uf}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`enderecos.${index}.complemento`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Complemento</FormLabel>
                    <FormControl>
                      <Input 
                        value={field.value || ""} 
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          atualizarEndereco(index, "complemento", e.target.value);
                        }} 
                        placeholder="Apto, Bloco, etc." 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center space-x-2 mt-4">
              <input 
                type="checkbox" 
                id={`principal-${index}`} 
                checked={endereco.principal} 
                onChange={(e) => atualizarEndereco(index, "principal", e.target.checked)} 
                className="rounded" 
              />
              <Label htmlFor={`principal-${index}`}>Endereço principal</Label>
            </div>
          </Card>
        ))}
        {(formData.enderecos || []).length === 0 && (
          <div>
            <div className="text-center text-muted-foreground py-8">
              Nenhum endereço cadastrado. Clique em "Adicionar Endereço" para começar.
            </div>
            {form.formState.errors.enderecos && (
              <p className="text-sm text-red-500 text-center">
                {form.formState.errors.enderecos.message}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
